import { useTranslation } from 'react-i18next';
import type { ReplitCareTeam, ReplitCareTeamMember } from '@/types/replit-dashboard';
import ExplainButton from './ExplainButton';
import { isBedsideRole, isFirstCallRole, roleKey } from './records-utils';

interface CareTeamSectionProps {
  careTeam: ReplitCareTeam[];
  onAskQuestion: (question: string) => void;
  variant: 'main' | 'rail';
}

type GroupId = 'bedside' | 'also';

/**
 * Care team split into the bedside/unit team and everyone else, with plain
 * role labels and static descriptions from i18n. No model text here.
 */
export default function CareTeamSection({ careTeam, onAskQuestion, variant }: CareTeamSectionProps) {
  const { t } = useTranslation();
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';

  const members: Array<{ member: ReplitCareTeamMember; index: number }> = (careTeam || [])
    .flatMap((team) => team.members || [])
    .map((member, index) => ({ member, index }));

  if (members.length === 0) {
    return <div className="text-sm text-gray-500 text-center py-4">{t('careTeam.noCareTeamInfo')}</div>;
  }

  const groups: Array<{ id: GroupId; label: string; rows: typeof members }> = [
    { id: 'bedside', label: t('records.groupBedsideTeam'), rows: members.filter(({ member }) => isBedsideRole(member.roles || [])) },
    { id: 'also', label: t('records.groupAlsoInvolved'), rows: members.filter(({ member }) => !isBedsideRole(member.roles || [])) },
  ];

  const renderRow = ({ member, index }: { member: ReplitCareTeamMember; index: number }) => {
    const roles = member.roles || [];
    const key = roles.map(roleKey).find((k): k is string => k !== null) ?? null;
    const roleLabel = key ? t(key) : roles.join(', ');
    return (
      <div key={index} className={`flex items-start justify-between gap-3 ${rowPadding} py-2.5`} data-testid={`row-team-member-${index}`}>
        <div className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-text-primary">{member.name}</span>
            {isFirstCallRole(roles) && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t('records.firstCall')}</span>
            )}
          </span>
          {roleLabel && <span className="block text-sm text-text-secondary mt-0.5">{roleLabel}</span>}
          {key && <span className="block text-sm text-text-secondary mt-0.5">{t(`${key}Desc`)}</span>}
        </div>
        <ExplainButton
          onClick={() => onAskQuestion(t('explain.careTeamMember', { name: member.name }))}
          ariaLabel={t('explain.careTeamMemberAriaLabel', { name: member.name })}
          testId={`button-explain-team-member-${index}`}
        />
      </div>
    );
  };

  return (
    <div>
      {groups.filter((group) => group.rows.length > 0).map((group) => (
        <div key={group.id} className="mb-4" data-testid={`careteam-group-${group.id}`}>
          <h3 className="text-sm font-semibold text-text-primary mb-2">{group.label}</h3>
          <div className="rounded-lg border bg-white divide-y">{group.rows.map(renderRow)}</div>
        </div>
      ))}
    </div>
  );
}
